import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  X,
} from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { useNotice } from '@/components/ConfirmDialog';
import { YearPicker } from '@/components/YearPicker';
import {
  INPUT_ATTRIBUTE_CARDS,
  PROFILE_ATTRIBUTE_CARDS,
  type InputCard as InputCardDef,
} from '@/data/profile-options';
import { resolveSystemDisplay, SYSTEM_CATALOG } from '@/data/systems';
import { SEASONAL_KINDS, isSeasonalKind, monthShort } from '@/data/seasonal';
import { now } from '@/db/ids';
import { yearBandFromYearBuilt } from '@/db/persist-onboarding';
import type { PropertyRow, SystemRow } from '@/db/types';
import { lookupClimate } from '@/data/climate-zip';
import { bumpDataRevision } from '@/lib/data-revision';
import { cancelPostOnboardingNudge } from '@/lib/notifications';
import { goBackOrTo, goTo } from '@/lib/nav';
import { resolveIcon } from '@/lib/rule-icon';
import { serializeMaterialArray } from '@/lib/property-attrs';
import {
  addSystem,
  getActiveProperty,
  getSystemsForProperty,
  isProfileFieldFilled,
  regenerateTasksForProperty,
  regenerateTasksWithDiff,
  updateSystem,
} from '@/lib/tasks';
import { webNoOutline } from '@/lib/web-style';

/**
 * "Tune my plan" — gamified card-stack walkthrough.
 *
 * The walkthrough handles TWO kinds of cards in one queue:
 *
 *   1. PROFILE ATTRIBUTE cards — basement, foundation, roof, exterior,
 *      hvac, water source, etc. Each card shows ONE attribute with
 *      chip options. One tap saves to the property row and advances.
 *      Built from PROFILE_ATTRIBUTE_CARDS in src/data/profile-options.ts,
 *      filtered to fields the user hasn't already filled.
 *
 *   2. SYSTEM SUBTYPE cards — water-heater type, fireplace type, roof
 *      shingle variation, etc. One per system whose catalog kind has
 *      subtype options that haven't been picked yet.
 *
 * Why one queue instead of two screens:
 *   - Mental model: it's all "tune the plan." Splitting attributes vs
 *     subtypes asks the user to learn an arbitrary distinction.
 *   - Pacing: card N of M with one progress dot row feels like real
 *     progress; two separate flows feel like two chores.
 *   - The order is intentional — attributes lead because they affect
 *     wider task-engine behavior. Subtypes are narrower (per-system).
 *
 * "Skip" advances without committing. The user can return later;
 * cards they already answered are filtered out next time.
 */

type AttrCard = {
  type: 'attribute';
  id: string;
  field: keyof PropertyRow;
  label: string;
  question: string;
  help: string;
  iconName: string;
  options: { value: string; label: string; description: string }[];
  /** Wave 59: multi-select cards (exterior, roof) toggle chips and
   *  commit via a Save button. First pick → `field` (legacy single
   *  column), full set → `multiField` (canonical JSON column). */
  multi?: boolean;
  multiField?: keyof PropertyRow;
};

type SystemCard = {
  type: 'system';
  id: string;
  system: SystemRow;
  subtypeOptions: { key: string; label: string; description: string }[];
  iconName: string;
  resolvedLabel: string;
  locationLabel: string;
  /** Wave 59: kinds where owning SEVERAL types at once is common
   *  (asphalt drive + gravel spur, cedar fence + chain-link run).
   *  Multi cards set the first pick as this system's subtype and
   *  create a sibling system instance per additional pick. */
  multi?: boolean;
};

/** System kinds where the Tune card should multi-select. Each extra
 *  pick becomes its own system instance so the planner schedules each
 *  material's tasks independently. */
const MULTI_SUBTYPE_KINDS = new Set(['driveway', 'fence']);

/**
 * Input cards — free-text or numeric fields that can't be answered with
 * chips. These mix into the same queue as chip-attribute and subtype
 * cards so the walkthrough can finish ALL profile gaps end-to-end
 * (Home nickname, address, year built, sqft, etc.) without dropping
 * the user into a different form.
 */
type InputCard = {
  type: 'input';
  id: string;
  def: InputCardDef;
};

/**
 * Seasonal card — asks the user for open / close months for systems
 * that have a clear cadence (pool, irrigation, AC, etc.). Storage is
 * on `system.active_start_month` and `system.active_end_month`; the
 * planner already respects those columns when generating tasks.
 *
 * "Year-round" is a valid answer — we persist NULL for both months
 * which the planner treats as "always active" (the existing default).
 */
type SeasonalCard = {
  type: 'seasonal';
  id: string;
  system: SystemRow;
  iconName: string;
  resolvedLabel: string;
  /** Catalog entry with hint copy + hemisphere-aware default months. */
  meta: import('@/data/seasonal').SeasonalKindMeta;
  /** Hemisphere the home is in — drives which default suggestion we
   *  pre-select (Apr-Oct in the northern hemisphere, the inverse in
   *  the southern). */
  hemisphere: 'northern' | 'southern';
};

type Card = AttrCard | SystemCard | InputCard | SeasonalCard;

export default function ProfileSetupScreen() {
  const router = useRouter();
  const db = useSQLiteContext();
  const [queue, setQueue] = useState<Card[]>([]);
  const [idx, setIdx] = useState(0);
  // Deep-link target: callers (Home tab "Add heating fuel" chip, My
  // Home blanks sheet, etc.) can pass ?field=heating_fuel so the
  // walkthrough opens to THAT card instead of the first in the queue.
  // Honored once on initial queue build; subsequent setQueue calls
  // (after a save advances naturally) ignore the param.
  const params = useLocalSearchParams<{ field?: string }>();
  const targetField = typeof params.field === 'string' ? params.field : null;
  const notice = useNotice();
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Active property id captured at fetch time. CRITICAL: all attribute
  // / input / address writes must scope to THIS id, not "the first
  // property" — the previous SQL used `ORDER BY created_at ASC LIMIT 1`
  // which silently rerouted picks to home #1 when the user was on
  // home #2/3/etc. From the user's perspective, the answers just
  // didn't save (the same blank card kept reappearing) because the
  // active home was still missing the field after the (mis-)write.
  const propertyIdRef = useRef<string | null>(null);

  // Card-stack slide animation. Picking an option slides the current
  // card off to the left while the next card slides in from the right.
  // Implementation: a single shared `translateX` value drives the
  // visible card.
  //
  // NOTE: we intentionally do NOT animate opacity any more. The white
  // card container is the only thing the user can see during the
  // transition, and fading it out (even to 0.6) read as the whole
  // panel "blinking out" rather than the content swapping. Slide-only
  // keeps the card solid; the horizontal motion alone is the signal
  // that the content has changed.
  const translateX = useSharedValue(0);

  const fetch = useCallback(async () => {
    const prop = await getActiveProperty(db);
    if (!prop) {
      setQueue([]);
      setLoaded(true);
      return;
    }
    propertyIdRef.current = prop.id;

    // Input cards first — name / address / year_built etc. These are
    // the "no chip can answer this" gaps and they tend to be the most
    // visible (home nickname shows up in the sidebar, address unlocks
    // the map). Lead with them so users see immediate payoff.
    const inputCards: InputCard[] = [];
    for (const def of INPUT_ATTRIBUTE_CARDS) {
      const current = prop[def.field];
      if (isProfileFieldFilled(current)) continue;
      inputCards.push({
        type: 'input',
        id: `input-${String(def.field)}`,
        def,
      });
    }

    // Attribute cards next — high-leverage structural fields where a
    // chip pick changes which tasks fire. Walk PROFILE_ATTRIBUTE_CARDS
    // in declared order so a user with all blanks sees the same
    // priority sequence as the Home "Finish profile" chip implies.
    const attrCards: AttrCard[] = [];
    for (const def of PROFILE_ATTRIBUTE_CARDS) {
      const current = prop[def.field];
      if (isProfileFieldFilled(current)) continue;
      attrCards.push({
        type: 'attribute',
        id: `attr-${String(def.field)}`,
        field: def.field,
        label: def.label,
        question: def.question,
        help: def.help,
        iconName: def.iconName,
        options: def.options,
        multi: def.multi,
        multiField: def.multiField,
      });
    }

    // System subtype cards last — narrower scope (per-system tuning).
    const all = await getSystemsForProperty(db, prop.id);
    const sysCards: SystemCard[] = [];
    for (const sys of all) {
      if (sys.subtype) continue;
      const meta = SYSTEM_CATALOG[sys.kind];
      if (!meta?.subtypes || meta.subtypes.length === 0) continue;
      const display = resolveSystemDisplay(sys);
      sysCards.push({
        type: 'system',
        id: `sys-${sys.id}`,
        system: sys,
        subtypeOptions: meta.subtypes,
        iconName: display.icon,
        resolvedLabel: display.label,
        locationLabel: display.location === 'inside' ? 'Inside' : 'Outside',
        multi: MULTI_SUBTYPE_KINDS.has(sys.kind),
      });
    }

    // Seasonal cards — for systems whose kind has a structural
    // open/close cadence (pool, irrigation, hose bibs, AC, etc.). Only
    // ask if the user hasn't ALREADY set the months — once they pick,
    // we skip the card on subsequent walkthroughs.
    const hemisphere = (prop.hemisphere === 'southern' ? 'southern' : 'northern') as
      | 'northern'
      | 'southern';
    const seasonalCards: SeasonalCard[] = [];
    for (const sys of all) {
      if (!isSeasonalKind(sys.kind)) continue;
      // Wave 43: include unless the user has EXPLICITLY confirmed the
      // months in Tune. Even when onboarding pre-fills smart defaults
      // (via defaultSeasonalRange in persist-onboarding), we still
      // want the user to review them once. After they save the card
      // (or pick Year-round), seasonal_confirmed_at is set and we
      // stop asking.
      if (sys.seasonal_confirmed_at != null) continue;
      const meta = SEASONAL_KINDS[sys.kind];
      if (!meta) continue;
      const display = resolveSystemDisplay(sys);
      seasonalCards.push({
        type: 'seasonal',
        id: `seasonal-${sys.id}`,
        system: sys,
        iconName: display.icon,
        resolvedLabel: display.label,
        meta,
        hemisphere,
      });
    }

    const built = [...inputCards, ...attrCards, ...sysCards, ...seasonalCards];
    setQueue(built);
    // Jump to the requested card if the URL contained ?field=…
    // matches an attribute or input card's field. Falls back to the
    // first card when the field isn't blank (already filled) or the
    // queue is empty.
    let startIdx = 0;
    if (targetField) {
      const found = built.findIndex(
        (c) =>
          (c.type === 'attribute' && String(c.field) === targetField) ||
          (c.type === 'input' && String(c.def.field) === targetField)
      );
      if (found >= 0) startIdx = found;
    }
    setIdx(startIdx);
    setLoaded(true);
  }, [db, targetField]);

  useFocusEffect(
    useCallback(() => {
      fetch();
    }, [fetch])
  );

  const SLIDE_DISTANCE = 360;

  /** Internal: jump the new card to the entry-from-right position then
   *  animate it home. Called after setIdx so the new card mounts at
   *  +SLIDE_DISTANCE and slides in cleanly. */
  const slideInFromRight = () => {
    translateX.value = SLIDE_DISTANCE;
    translateX.value = withTiming(0, {
      duration: 280,
      easing: Easing.out(Easing.cubic),
    });
  };

  /** Internal: same, but entering from the LEFT (used when the user
   *  taps Back, so the motion direction matches "going backward"). */
  const slideInFromLeft = () => {
    translateX.value = -SLIDE_DISTANCE;
    translateX.value = withTiming(0, {
      duration: 280,
      easing: Easing.out(Easing.cubic),
    });
  };

  /** How many cards got a real answer this session — drives the
   *  auto-replan on exit (Wave 66). Skips don't count. */
  const answeredRef = useRef(0);

  /**
   * Leave the walkthrough THROUGH a replan (Wave 66, user report: "I
   * expected a replan right after"). Every chip pick changes what the
   * planner would schedule, but the schedule itself was only refreshed
   * when the user later found the Replan button — tuning felt inert.
   * Now: any session with ≥1 answer regenerates the plan on the way
   * out and says what changed. Zero answers exits instantly.
   */
  const exitWithReplan = useCallback(async (dest: string) => {
    const pid = propertyIdRef.current;
    if (!pid || answeredRef.current === 0) {
      goTo(router, dest);
      return;
    }
    setSaving(true);
    try {
      const diff = await regenerateTasksWithDiff(db, pid);
      bumpDataRevision();
      const parts: string[] = [];
      if (diff.added > 0) parts.push(`${diff.added} task${diff.added === 1 ? '' : 's'} added`);
      if (diff.removed > 0) parts.push(`${diff.removed} retired`);
      if (diff.rescheduled > 0) parts.push(`${diff.rescheduled} re-timed`);
      goTo(router, dest);
      if (parts.length > 0) {
        void notice({
          title: 'Plan retuned',
          message: `Your picks just reshaped the schedule — ${parts.join(', ')}.`,
          tone: 'info',
        });
      }
    } catch (err) {
      console.warn('post-tune replan failed', err);
      goTo(router, dest);
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, router]);

  const advance = useCallback(() => {
    const next = idx + 1;
    if (next >= queue.length) {
      // The 24h "Got 2 minutes for Homestead?" push was scheduled at
      // the end of onboarding to drag users back to the Tune
      // walkthrough. They just answered the chip questions — firing
      // that push later would tell them to do work they've already
      // done. Cancel it. Safe no-op when no push exists for this
      // property (e.g. web, denied permission, already fired).
      const pid = propertyIdRef.current;
      if (pid) void cancelPostOnboardingNudge(pid);
      // Done — replan with the fresh answers, then back to My Home so
      // the user sees the updated schedule and health card.
      void exitWithReplan('/(tabs)/my-home');
      return;
    }
    setIdx(next);
    slideInFromRight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, queue.length, router, exitWithReplan]);

  /** Step backward one card. Disabled on the first card. */
  const goBackOneCard = useCallback(() => {
    if (idx === 0 || saving) return;
    setIdx(idx - 1);
    slideInFromLeft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, saving]);

  const animatedCardStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }));

  /**
   * Pick a value on the current card and animate forward. Branches by
   * card type — attribute writes to property row, subtype writes to
   * system row. Either way the visual flow is identical.
   */
  const pick = (card: Card, value: string) => {
    if (saving) return;
    setSaving(true);
    // Kick the DB write off RIGHT NOW so a fast tab-close or app
    // background mid-animation never drops the user's pick. The save
    // and the animation are independent — the animation just controls
    // when the new card appears, while the data is already on disk.
    void commitOnly(card, value);
    // Slide the current card off to the LEFT. On completion, advance
    // to the next card (which will be entered via slideInFromRight).
    translateX.value = withTiming(
      -SLIDE_DISTANCE,
      { duration: 240, easing: Easing.in(Easing.cubic) },
      () => {
        runOnJS(finishPick)();
      }
    );
  };

  const finishPick = () => {
    setSaving(false);
    advance();
  };

  /**
   * Address-card commit. Writes multiple property columns at once
   * (address, lat, lon, plus zip + climate_zone if the Places detail
   * returns them) since they all change together when a user picks a
   * suggestion. Used only by the InputCard.address branch.
   */
  const commitAddressAndAdvance = async (
    address: string,
    lat: number | null,
    lon: number | null,
    zip: string | null
  ) => {
    if (saving) return;
    answeredRef.current += 1;
    setSaving(true);
    const pid = propertyIdRef.current;
    try {
      if (pid) {
        const cz = zip ? (lookupClimate(zip)?.zone ?? null) : null;
        await db.runAsync(
          `UPDATE property
           SET address = ?, lat = ?, lon = ?,
               zip = COALESCE(?, zip),
               climate_zone = COALESCE(?, climate_zone),
               updated_at = ?
           WHERE id = ?`,
          address,
          lat,
          lon,
          zip,
          cz,
          now(),
          pid
        );
        bumpDataRevision();
      }
    } catch (err) {
      console.warn('profile-setup address commit failed', err);
    }
    // Same slide-out → advance pattern as `pick`.
    translateX.value = withTiming(
      -SLIDE_DISTANCE,
      { duration: 240, easing: Easing.in(Easing.cubic) },
      () => {
        runOnJS(finishPick)();
      }
    );
  };

  /** Pure save — does the DB write for any card type and bumps the
   *  data revision. Does NOT advance the queue or touch animation
   *  state. Called immediately from `pick` so the user's answer hits
   *  the row at tap time, not at animation-finish time.
   *
   *  Uses propertyIdRef captured at fetch() time so writes always go to
   *  the ACTIVE property. Earlier versions used
   *  `WHERE id = (SELECT id FROM property ORDER BY created_at ASC LIMIT 1)`
   *  which silently routed every attribute/input pick to the user's
   *  FIRST home in multi-home setups — picks on home #2 "didn't save"
   *  because the right column on the right home was never touched. */
  const commitOnly = async (card: Card, value: string) => {
    answeredRef.current += 1;
    const pid = propertyIdRef.current;
    if (!pid) {
      console.warn('profile-setup commit skipped — no active property');
      return;
    }
    try {
      if (card.type === 'attribute') {
        // Column names mirror the field key (already a PropertyRow key).
        const col = String(card.field);
        await db.runAsync(
          `UPDATE property SET ${col} = ?, updated_at = ? WHERE id = ?`,
          value,
          now(),
          pid
        );
        // Basement → foundation derivation. The foundation Tune card
        // was dropped (felt redundant to basement) and foundation is
        // now inferred from the basement pick when it's still null.
        // We don't overwrite an existing foundation — a user who set
        // pier explicitly in Edit Home keeps it. Mapping:
        //   basement=none       → foundation=slab
        //   basement=crawl      → foundation=crawl
        //   basement=*basement* → foundation=basement
        if (card.field === 'basement') {
          const derivedFoundation =
            value === 'none'
              ? 'slab'
              : value === 'crawl'
              ? 'crawl'
              : value === 'finished' || value === 'unfinished' || value === 'partial'
              ? 'basement'
              : null;
          if (derivedFoundation) {
            await db.runAsync(
              `UPDATE property
                 SET foundation = COALESCE(foundation, ?), updated_at = ?
               WHERE id = ?`,
              derivedFoundation,
              now(),
              pid
            );
          }
        }
        // Tell subscribers (Home ring, sidebar, weekly-prompt sweep)
        // that the property row changed. updateSystem already does
        // this internally, so the subtype branch is covered.
        bumpDataRevision();
      } else if (card.type === 'system') {
        await updateSystem(db, card.system.id, { subtype: value });
      } else if (card.type === 'input') {
        // Text or numeric value, NOT the address branch (that goes
        // through commitAddressAndAdvance which writes multiple cols).
        const col = String(card.def.field);
        const stored: string | number =
          card.def.kind === 'number' || card.def.kind === 'year'
            ? Number(value)
            : value;
        // Special case: when zip changes via the standalone zip input
        // card, derive climate_zone too so the climate signal updates
        // immediately rather than waiting for a future replan.
        if (card.def.field === 'zip' && typeof value === 'string' && value.trim()) {
          const cz = lookupClimate(value.trim())?.zone ?? null;
          await db.runAsync(
            `UPDATE property
             SET zip = ?, climate_zone = COALESCE(?, climate_zone),
                 updated_at = ?
             WHERE id = ?`,
            stored,
            cz,
            now(),
            pid
          );
        } else if (card.def.field === 'year_built') {
          // Keep the coarse era bucket in lockstep (Wave 74 bug: Tune
          // saved the exact year but the home still showed its old
          // 1960s–1980s band everywhere the bucket renders).
          await db.runAsync(
            `UPDATE property SET year_built = ?, year_band = ?, updated_at = ? WHERE id = ?`,
            stored,
            yearBandFromYearBuilt(typeof stored === 'number' ? stored : null),
            now(),
            pid
          );
        } else {
          await db.runAsync(
            `UPDATE property SET ${col} = ?, updated_at = ? WHERE id = ?`,
            stored,
            now(),
            pid
          );
        }
        bumpDataRevision();
      }
    } catch (err) {
      console.warn('profile-setup commit failed', err);
    }
    // Note: NO advance() here anymore. pick() now drives the animation
    // and calls finishPick on completion; commitOnly is the pure
    // save half of the split.
  };

  /**
   * Seasonal-card commit. Writes active_start_month + active_end_month
   * on the system row. `null` values are valid — they mean "year-round"
   * (the planner treats absent bounds as "always active"). Slides the
   * card off and advances on success, same animation pattern as pick().
   */
  const commitSeasonal = async (
    card: SeasonalCard,
    openMonth: number | null,
    closeMonth: number | null
  ) => {
    if (saving) return;
    answeredRef.current += 1;
    setSaving(true);
    try {
      // Wave 43: stamp seasonal_confirmed_at so we stop surfacing
      // this system's card on subsequent Tune passes. The user has
      // explicitly reviewed the open/close window.
      await updateSystem(db, card.system.id, {
        active_start_month: openMonth,
        active_end_month: closeMonth,
        seasonal_confirmed_at: now(),
      });
      bumpDataRevision();
    } catch (err) {
      console.warn('profile-setup seasonal commit failed', err);
    }
    translateX.value = withTiming(
      -SLIDE_DISTANCE,
      { duration: 240, easing: Easing.in(Easing.cubic) },
      () => {
        runOnJS(finishPick)();
      }
    );
  };

  /**
   * Multi-select commit (Wave 59). Two flavors sharing one entry point:
   *
   *  - attribute + multiField: first pick → the legacy single-value
   *    column, full set → the canonical JSON column (same contract as
   *    Edit Home → Structure and lib/property-attrs).
   *  - system (driveway, fence): first pick becomes THIS system's
   *    subtype; each additional pick creates a sibling system instance
   *    so the planner schedules each material's tasks independently.
   *    New instances need a task pass, so we regenerate before sliding.
   */
  const commitMulti = async (card: AttrCard | SystemCard, values: string[]) => {
    if (saving || values.length === 0) return;
    answeredRef.current += 1;
    setSaving(true);
    const pid = propertyIdRef.current;
    try {
      if (card.type === 'attribute' && card.multiField && pid) {
        await db.runAsync(
          `UPDATE property SET ${String(card.field)} = ?, ${String(card.multiField)} = ?, updated_at = ? WHERE id = ?`,
          values[0],
          serializeMaterialArray(values),
          now(),
          pid
        );
        bumpDataRevision();
      } else if (card.type === 'system') {
        await updateSystem(db, card.system.id, { subtype: values[0] });
        if (values.length > 1 && pid) {
          for (const extra of values.slice(1)) {
            await addSystem(db, {
              propertyId: pid,
              kind: card.system.kind,
              location: card.system.location,
              subtype: extra,
              is_active: 1,
              active_start_month: card.system.active_start_month,
              active_end_month: card.system.active_end_month,
            });
          }
          await regenerateTasksForProperty(db, pid);
        }
      }
    } catch (err) {
      console.warn('profile-setup multi commit failed', err);
    }
    translateX.value = withTiming(
      -SLIDE_DISTANCE,
      { duration: 240, easing: Easing.in(Easing.cubic) },
      () => {
        runOnJS(finishPick)();
      }
    );
  };

  const onSkip = () => {
    if (saving) return;
    advance();
  };

  if (!loaded) {
    return <SafeAreaView style={styles.container} edges={['top', 'bottom']} />;
  }

  // Empty / done state — no eligible cards left.
  if (queue.length === 0) {
    return (
      <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
        <View style={styles.topbar}>
          <Pressable
            onPress={() => goBackOrTo(router, '/(tabs)/my-home')}
            style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
            hitSlop={12}>
            <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
          </Pressable>
          <Text style={styles.topbarTitle}>Tune my plan</Text>
          <View style={{ width: 36 }} />
        </View>
        <View style={styles.emptyWrap}>
          <View style={styles.emptyIcon}>
            <Sparkles size={32} color={Palette.honeyDeep} strokeWidth={1.7} />
          </View>
          <Text style={styles.emptyTitle}>Nothing left to tune</Text>
          <Text style={styles.emptyBody}>
            Every profile field is filled and every system that needed a
            variation has one picked. Add more systems from the My Home
            tab if you&apos;d like more options here.
          </Text>
          <Pressable
            onPress={() => goTo(router, '/(tabs)/my-home')}
            style={({ pressed }) => [styles.primaryBtn, pressed && { opacity: 0.9 }]}>
            <Text style={styles.primaryBtnText}>Back to My Home</Text>
          </Pressable>
        </View>
      </SafeAreaView>
    );
  }

  const current = queue[idx];
  // Card icons come from one of three places depending on type.
  const iconName =
    current.type === 'input' ? current.def.iconName : current.iconName;
  const Icon = resolveIcon(iconName);

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      {/*
        Topbar — title + close (X) only. The back affordance lives below
        as a dedicated "Back" link tucked under the topbar on the left.
        Splitting close (always exits) from back (steps one card)
        prevents the regression where users tapping the top-left arrow
        expected to scrub backward but got dumped out of the flow.
      */}
      <View style={styles.topbar}>
        <View style={{ width: 36 }} />
        <Text style={styles.topbarTitle}>Tune my plan</Text>
        <Pressable
          onPress={() => void exitWithReplan('/(tabs)/my-home')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}
          accessibilityLabel="Close">
          <X size={20} color={Palette.ink} strokeWidth={2} />
        </Pressable>
      </View>

      {/* Back link — moved here from the footer so it sits in the
          natural top-left "back" zone, just under the topbar. Only
          enabled when there's a previous card to return to. */}
      <View style={styles.backRow}>
        <Pressable
          onPress={goBackOneCard}
          disabled={idx === 0 || saving}
          accessibilityLabel="Previous card"
          style={({ pressed }) => [
            styles.cardBackBtn,
            pressed && { opacity: 0.85 },
            (idx === 0 || saving) && { opacity: 0.35 },
          ]}>
          <ChevronLeft size={16} color={Palette.inkSoft} strokeWidth={2} />
          <Text style={styles.cardBackBtnText}>Back</Text>
        </Pressable>
      </View>

      {/* Progress strip — sense of how many cards are left. */}
      <View style={styles.progress}>
        <Text style={styles.progressText}>
          Card {idx + 1} of {queue.length}
        </Text>
        <View style={styles.dots}>
          {queue.map((_, i) => (
            <View
              key={i}
              style={[
                styles.dot,
                i === idx && styles.dotActive,
                i < idx && styles.dotPast,
              ]}
            />
          ))}
        </View>
      </View>

      {/* Card slides off-left on pick, then the next card slides in
          from the right. We drive a single `translateX` shared value
          (see pick / advance) so there's exactly one node animating —
          no double-render between exit + entry that previous versions
          had. Works on web too: Reanimated falls back to rAF there. */}
      <Animated.View style={[styles.cardWrap, animatedCardStyle]}>
        <View style={styles.card}>
          <View style={styles.systemHeader}>
            <View
              style={[
                styles.systemIconBubble,
                (current.type === 'attribute' || current.type === 'input') &&
                  styles.attrIconBubble,
              ]}>
              <Icon
                size={32}
                color={
                  current.type === 'attribute' || current.type === 'input'
                    ? Palette.honeyDeep
                    : Palette.forest
                }
                strokeWidth={1.7}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.systemName}>
                {current.type === 'attribute'
                  ? current.question
                  : current.type === 'input'
                  ? current.def.question
                  : current.type === 'seasonal'
                  ? `When is your ${current.resolvedLabel.toLowerCase()} in season?`
                  : current.resolvedLabel}
              </Text>
              <Text style={styles.systemLoc}>
                {current.type === 'attribute' || current.type === 'input'
                  ? 'About your home'
                  : current.type === 'seasonal'
                  ? `${current.resolvedLabel} · seasonal cadence`
                  : current.locationLabel}
              </Text>
            </View>
          </View>

          <Text style={styles.questionLabel}>
            {current.type === 'attribute'
              ? current.multi
                ? 'PICK ALL THAT APPLY'
                : 'PICK ONE'
              : current.type === 'input'
              ? 'TYPE IT IN'
              : current.type === 'seasonal'
              ? 'PICK OPEN + CLOSE'
              : current.multi
              ? 'PICK ALL THAT APPLY'
              : 'WHICH KIND?'}
          </Text>
          <Text style={styles.questionHelp}>
            {current.type === 'attribute'
              ? current.help
              : current.type === 'input'
              ? current.def.help
              : current.type === 'seasonal'
              ? current.meta.hint
              : `Pick one to tune the tasks for this ${current.resolvedLabel.toLowerCase()}. Skip if you're not sure — you can come back any time.`}
          </Text>

          {current.type === 'input' ? (
            <InputCardBody
              key={current.id}
              card={current}
              saving={saving}
              onCommit={(value) => pick(current, value)}
              onCommitAddress={commitAddressAndAdvance}
            />
          ) : current.type === 'seasonal' ? (
            <SeasonalCardBody
              key={current.id}
              card={current}
              saving={saving}
              onCommit={(open, close) => commitSeasonal(current, open, close)}
            />
          ) : current.multi ? (
            <MultiChoiceBody
              key={current.id}
              options={
                current.type === 'attribute'
                  ? current.options.map((o) => ({
                      key: o.value,
                      label: o.label,
                      description: o.description,
                    }))
                  : current.subtypeOptions
              }
              saving={saving}
              onCommit={(values) => commitMulti(current, values)}
            />
          ) : (
            <ScrollView
              style={styles.chipScroll}
              contentContainerStyle={styles.chipList}
              showsVerticalScrollIndicator={false}>
              {(current.type === 'attribute'
                ? current.options.map((o) => ({ key: o.value, label: o.label, description: o.description }))
                : current.subtypeOptions
              ).map((opt) => (
                <Pressable
                  key={opt.key}
                  onPress={() => pick(current, opt.key)}
                  disabled={saving}
                  style={({ pressed, hovered }) => [
                    styles.choiceCard,
                    hovered && styles.choiceCardHover,
                    pressed && styles.choiceCardPressed,
                    saving && { opacity: 0.6 },
                  ]}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.choiceTitle}>{opt.label}</Text>
                    <Text style={styles.choiceDesc}>{opt.description}</Text>
                  </View>
                  <ChevronRight size={16} color={Palette.forest} strokeWidth={2} />
                </Pressable>
              ))}
            </ScrollView>
          )}
        </View>
      </Animated.View>

      {/* Footer: Skip-only (right-aligned). Back was moved up to the
          top-left strip beneath the topbar — see <View style={styles.backRow}>
          above — so the card has exactly one Back affordance instead
          of two. */}
      <View style={styles.footer}>
        <View style={{ flex: 1 }} />
        <Pressable
          onPress={onSkip}
          disabled={saving}
          style={({ pressed }) => [
            styles.skipBtn,
            pressed && { opacity: 0.85 },
            saving && { opacity: 0.45 },
          ]}>
          <Text style={styles.skipBtnText}>Skip this one →</Text>
        </Pressable>
      </View>
    </SafeAreaView>
  );
}

/**
 * Body of an InputCard — text / number / address input + Save button.
 * Lifted out so the input has its own state (controlled text doesn't
 * play well with the parent's animated remount-on-advance).
 *
 * Pressing Save calls `onCommit(value)` for text+number cards, or
 * `onCommitAddress(...)` for address cards (which writes multiple
 * property columns at once via the Places detail).
 */
function InputCardBody({
  card,
  saving,
  onCommit,
  onCommitAddress,
}: {
  card: InputCard;
  saving: boolean;
  onCommit: (value: string) => void;
  onCommitAddress: (
    address: string,
    lat: number | null,
    lon: number | null,
    zip: string | null
  ) => void;
}) {
  const def = card.def;
  const [value, setValue] = useState('');

  /** Validate + commit for text / number cards. Numbers must be within
   *  the optional [min, max] bounds the card defined. */
  const trySave = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    if (def.kind === 'number') {
      const n = Number(trimmed);
      if (!Number.isFinite(n)) return;
      if (def.min != null && n < def.min) return;
      if (def.max != null && n > def.max) return;
    }
    onCommit(trimmed);
  };

  return (
    <View style={inputStyles.wrap}>
      {def.kind === 'address' ? (
        <AddressAutocomplete
          value={value}
          onChangeText={setValue}
          onPick={(detail) => {
            setValue(detail.address);
            // Commit immediately on Places pick — the suggestion list
            // already represents user intent. No need to make them tap
            // Save afterward.
            onCommitAddress(
              detail.address,
              detail.lat ?? null,
              detail.lon ?? null,
              detail.zip ?? null
            );
          }}
          placeholder={def.placeholder}
        />
      ) : def.kind === 'year' ? (
        // Year-pick cards open the YearPicker scroll list. The user
        // can't type garbage; the picker enforces a sane range and
        // auto-extends to currentYear + 1 every January.
        <YearPicker
          value={value ? Number(value) : null}
          onChange={(y) => {
            if (y == null) {
              setValue('');
              return;
            }
            const next = String(y);
            setValue(next);
            // Commit immediately — the scroll-pick gesture is intent
            // enough; making the user tap a separate Save button on
            // top of it just adds friction.
            onCommit(next);
          }}
          placeholder={def.placeholder}
          minYear={def.min ?? 1700}
          title={def.label}
        />
      ) : (
        <TextInput
          value={value}
          onChangeText={setValue}
          placeholder={def.placeholder}
          placeholderTextColor={Palette.inkFaint}
          inputMode={def.kind === 'number' ? 'numeric' : 'text'}
          autoCapitalize={def.kind === 'text' ? 'words' : 'none'}
          onSubmitEditing={trySave}
          style={[inputStyles.input, webNoOutline]}
          editable={!saving}
        />
      )}

      {def.kind === 'text' || def.kind === 'number' ? (
        <Pressable
          onPress={trySave}
          disabled={saving || !value.trim()}
          style={({ pressed }) => [
            inputStyles.saveBtn,
            pressed && { opacity: 0.9 },
            (saving || !value.trim()) && { opacity: 0.4 },
          ]}>
          <Text style={inputStyles.saveBtnText}>Save & continue</Text>
        </Pressable>
      ) : def.kind === 'address' ? (
        <Text style={inputStyles.addressHint}>
          Pick a suggestion to fill ZIP + map automatically.
        </Text>
      ) : null}
    </View>
  );
}

/**
 * Multi-select card body (Wave 59) — checkbox-style option rows plus a
 * Save button. Used for the exterior / roof attribute cards and for
 * system-subtype cards whose kind commonly has several types at once
 * (driveway, fence). Selection order matters: the FIRST pick becomes
 * the primary / this-system value, so we keep values in tap order.
 */
function MultiChoiceBody({
  options,
  saving,
  onCommit,
}: {
  options: { key: string; label: string; description: string }[];
  saving: boolean;
  onCommit: (values: string[]) => void;
}) {
  const [picked, setPicked] = useState<string[]>([]);

  const toggle = (key: string) => {
    setPicked((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  return (
    <View style={{ flex: 1 }}>
      <ScrollView
        style={styles.chipScroll}
        contentContainerStyle={styles.chipList}
        showsVerticalScrollIndicator={false}>
        {options.map((opt) => {
          const sel = picked.includes(opt.key);
          return (
            <Pressable
              key={opt.key}
              onPress={() => toggle(opt.key)}
              disabled={saving}
              style={({ pressed, hovered }) => [
                styles.choiceCard,
                hovered && styles.choiceCardHover,
                sel && styles.multiChoiceSel,
                pressed && { opacity: 0.9 },
                saving && { opacity: 0.6 },
              ]}>
              <View style={[styles.multiTick, sel && styles.multiTickSel]}>
                {sel ? <Check size={12} color={Palette.cream} strokeWidth={2.6} /> : null}
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.choiceTitle}>{opt.label}</Text>
                <Text style={styles.choiceDesc}>{opt.description}</Text>
              </View>
            </Pressable>
          );
        })}
      </ScrollView>
      <Pressable
        onPress={() => onCommit(picked)}
        disabled={saving || picked.length === 0}
        style={({ pressed }) => [
          styles.multiSaveBtn,
          (saving || picked.length === 0) && { opacity: 0.45 },
          pressed && picked.length > 0 && { transform: [{ scale: 0.98 }] },
        ]}>
        <Check size={16} color={Palette.cream} strokeWidth={2.4} />
        <Text style={styles.multiSaveText}>
          {saving
            ? 'Saving…'
            : picked.length === 0
            ? 'Pick at least one'
            : picked.length === 1
            ? 'Save'
            : `Save · ${picked.length} picked`}
        </Text>
      </Pressable>
    </View>
  );
}

/**
 * Seasonal-card body — two chip rows (open month, close month) plus a
 * "Year-round" shortcut + "Use suggested" button that fills in the
 * hemisphere-aware defaults from the catalog. Save commits to the
 * `system.active_*_month` columns and slides the card forward.
 */
function SeasonalCardBody({
  card,
  saving,
  onCommit,
}: {
  card: SeasonalCard;
  saving: boolean;
  onCommit: (open: number | null, close: number | null) => void;
}) {
  const suggested = card.meta.defaults[card.hemisphere];
  // Initial values: prefer the system's CURRENT months (set by
  // onboarding or a prior Tune save) so the user sees their existing
  // setup pre-selected. Fall back to the hemisphere-aware suggestion
  // when both columns are null. This makes the card feel like a
  // "review what's set" surface rather than a "start from scratch" one.
  const [openMonth, setOpenMonth] = useState<number | null>(
    card.system.active_start_month ?? suggested.open
  );
  const [closeMonth, setCloseMonth] = useState<number | null>(
    card.system.active_end_month ?? suggested.close
  );
  const yearRound = openMonth == null && closeMonth == null;

  return (
    <View style={seasonalStyles.wrap}>
      <View style={seasonalStyles.row}>
        <Text style={seasonalStyles.rowLabel}>Open in</Text>
        <MonthChipRow selected={openMonth} disabled={saving} onPick={setOpenMonth} />
      </View>
      <View style={seasonalStyles.row}>
        <Text style={seasonalStyles.rowLabel}>Close in</Text>
        <MonthChipRow selected={closeMonth} disabled={saving} onPick={setCloseMonth} />
      </View>

      <View style={seasonalStyles.suggestRow}>
        <Pressable
          onPress={() => {
            setOpenMonth(suggested.open);
            setCloseMonth(suggested.close);
          }}
          disabled={saving}
          style={({ pressed }) => [seasonalStyles.suggestBtn, pressed && { opacity: 0.85 }]}>
          <Text style={seasonalStyles.suggestBtnText}>
            Use {monthShort(suggested.open)}–{monthShort(suggested.close)}
          </Text>
        </Pressable>
        <Pressable
          onPress={() => {
            setOpenMonth(null);
            setCloseMonth(null);
          }}
          disabled={saving}
          style={({ pressed }) => [
            seasonalStyles.suggestBtn,
            yearRound && seasonalStyles.suggestBtnActive,
            pressed && { opacity: 0.85 },
          ]}>
          <Text
            style={[
              seasonalStyles.suggestBtnText,
              yearRound && seasonalStyles.suggestBtnTextActive,
            ]}>
            Year-round
          </Text>
        </Pressable>
      </View>

      <Pressable
        onPress={() => onCommit(openMonth, closeMonth)}
        disabled={saving}
        style={({ pressed }) => [
          seasonalStyles.saveBtn,
          pressed && { opacity: 0.9 },
          saving && { opacity: 0.45 },
        ]}>
        <Text style={seasonalStyles.saveBtnText}>
          {yearRound ? 'Save · always active' : `Save · ${monthShort(openMonth!)}–${monthShort(closeMonth!)}`}
        </Text>
      </Pressable>
    </View>
  );
}

/** Fixed chip geometry so the centering math below is exact. */
const MONTH_CHIP_W = 56;
const MONTH_CHIP_GAP = 6;

/**
 * Horizontal month strip that auto-scrolls so the SELECTED month sits
 * centered when the card appears (Wave 59 — irrigation's default close
 * month was off-screen, so the pre-selection was invisible). Centers
 * once per layout; after that the user's own scrolling wins.
 */
function MonthChipRow({
  selected,
  disabled,
  onPick,
}: {
  selected: number | null;
  disabled: boolean;
  onPick: (m: number) => void;
}) {
  const ref = useRef<ScrollView>(null);
  const centeredRef = useRef(false);
  const [viewportW, setViewportW] = useState(0);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  useEffect(() => {
    if (centeredRef.current || !viewportW || selected == null) return;
    centeredRef.current = true;
    const chipCenter = (selected - 1) * (MONTH_CHIP_W + MONTH_CHIP_GAP) + MONTH_CHIP_W / 2;
    const x = Math.max(0, chipCenter - viewportW / 2);
    // Next frame — scrollTo before content lays out is a no-op on native.
    requestAnimationFrame(() => ref.current?.scrollTo({ x, animated: false }));
  }, [viewportW, selected]);

  return (
    <ScrollView
      ref={ref}
      horizontal
      showsHorizontalScrollIndicator={false}
      onLayout={(e) => setViewportW(e.nativeEvent.layout.width)}
      contentContainerStyle={seasonalStyles.chipRow}>
      {months.map((m) => (
        <Pressable
          key={m}
          onPress={() => onPick(m)}
          disabled={disabled}
          style={({ pressed }) => [
            seasonalStyles.monthChip,
            selected === m && seasonalStyles.monthChipActive,
            pressed && { opacity: 0.85 },
          ]}>
          <Text
            style={[
              seasonalStyles.monthChipText,
              selected === m && seasonalStyles.monthChipTextActive,
            ]}>
            {monthShort(m)}
          </Text>
        </Pressable>
      ))}
    </ScrollView>
  );
}

const seasonalStyles = StyleSheet.create({
  wrap: { flex: 1, marginTop: 14, gap: 14 },
  row: { gap: 6 },
  rowLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    color: Palette.inkSoft,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    paddingHorizontal: 2,
  },
  chipRow: { gap: MONTH_CHIP_GAP, paddingVertical: 2 },
  monthChip: {
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
    // Fixed width (not minWidth) — MonthChipRow's centering math
    // depends on every chip being exactly this wide.
    width: MONTH_CHIP_W,
    alignItems: 'center',
  },
  monthChipActive: {
    backgroundColor: '#EAF0EA',
    borderColor: Palette.forest,
  },
  monthChipText: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 13.5,
    color: Palette.inkSoft,
    letterSpacing: 0.2,
  },
  monthChipTextActive: {
    color: Palette.forest,
    fontFamily: Fonts.sansBold,
  },
  suggestRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
  },
  suggestBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 999,
    backgroundColor: Palette.sand,
    borderWidth: 1,
    borderColor: Palette.line,
    alignItems: 'center',
  },
  suggestBtnActive: {
    backgroundColor: '#EAF0EA',
    borderColor: Palette.forest,
  },
  suggestBtnText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.inkSoft,
    letterSpacing: 0.2,
  },
  suggestBtnTextActive: { color: Palette.forest },
  saveBtn: {
    marginTop: 'auto',
    paddingVertical: 14,
    borderRadius: 999,
    backgroundColor: Palette.forest,
    alignItems: 'center',
  },
  saveBtnText: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.cream,
    letterSpacing: 0.2,
  },
});

const inputStyles = StyleSheet.create({
  wrap: {
    flex: 1,
    marginTop: 14,
    gap: 12,
  },
  input: {
    fontFamily: Fonts.sansBold,
    fontSize: 22,
    color: Palette.ink,
    paddingHorizontal: 14,
    paddingVertical: 14,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
    borderRadius: Radius.lg,
  },
  saveBtn: {
    paddingVertical: 14,
    borderRadius: 14,
    backgroundColor: Palette.forest,
    alignItems: 'center',
  },
  saveBtnText: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.cream,
    letterSpacing: 0.2,
  },
  addressHint: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkFaint,
    textAlign: 'center',
  },
});

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },

  // Strip under the topbar that holds the card-level Back link, left-
  // aligned in the natural "back" zone of the screen.
  backRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.three,
    paddingTop: 2,
    paddingBottom: 4,
  },

  progress: {
    paddingHorizontal: 22, paddingVertical: Spacing.three,
    alignItems: 'center', gap: 8,
  },
  progressText: {
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkFaint,
    letterSpacing: 0.6,
  },
  dots: { flexDirection: 'row', gap: 5, flexWrap: 'wrap', justifyContent: 'center' },
  dot: {
    width: 8, height: 8, borderRadius: 4,
    backgroundColor: Palette.sandDeep,
  },
  dotActive: { backgroundColor: Palette.forest, width: 22 },
  dotPast: { backgroundColor: Palette.forestLight },

  cardWrap: {
    flex: 1,
    paddingHorizontal: Spacing.four,
  },
  card: {
    flex: 1,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl,
    padding: 22,
    shadowColor: '#000',
    shadowOpacity: 0.08,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 4 },
    elevation: 6,
  },
  systemHeader: {
    flexDirection: 'row', alignItems: 'center', gap: 14,
    marginBottom: 18,
  },
  systemIconBubble: {
    width: 62, height: 62, borderRadius: 20,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  // Honey-tinted bubble for attribute cards so the user gets a visual
  // cue when the card switches modes (attribute ↔ subtype).
  attrIconBubble: {
    backgroundColor: 'rgba(225,163,61,0.18)',
  },
  systemName: {
    fontFamily: Fonts.serifBold, fontSize: 22, color: Palette.ink,
    letterSpacing: -0.3,
  },
  systemLoc: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 13.5, color: Palette.inkSoft,
  },

  questionLabel: {
    fontFamily: Fonts.sansBold, fontSize: 12,
    color: Palette.inkSoft, letterSpacing: 0.7,
  },
  questionHelp: {
    marginTop: 6,
    fontFamily: Fonts.sansMedium, fontSize: 13.5, color: Palette.inkSoft,
    lineHeight: 18,
  },

  chipScroll: {
    flex: 1,
    marginTop: 14,
  },
  chipList: {
    paddingBottom: 8,
    gap: 8,
  },
  choiceCard: {
    flexDirection: 'row', alignItems: 'center',
    gap: 10,
    paddingHorizontal: 14, paddingVertical: 13,
    borderRadius: 14,
    borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand,
  },
  choiceCardHover: {
    borderColor: Palette.forestLight,
    backgroundColor: '#F4F7F2',
  },
  choiceCardPressed: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
    transform: [{ scale: 0.98 }],
  },
  choiceTitle: {
    fontFamily: Fonts.sansBold, fontSize: 14.5,
    color: Palette.ink,
    letterSpacing: -0.1,
  },
  choiceDesc: {
    marginTop: 3,
    fontFamily: Fonts.sansMedium, fontSize: 13,
    color: Palette.inkSoft, lineHeight: 16,
  },

  // Multi-select (Wave 59) — checkbox tick + selected row tint + the
  // Save bar under the option list.
  multiChoiceSel: {
    borderColor: Palette.forest,
    backgroundColor: '#EEF3EE',
  },
  multiTick: {
    width: 22, height: 22, borderRadius: 7,
    borderWidth: 2, borderColor: Palette.sandDeep,
    backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  multiTickSel: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
  },
  multiSaveBtn: {
    marginTop: 10,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8,
    backgroundColor: Palette.forest,
    paddingVertical: 14, borderRadius: 14,
  },
  multiSaveText: {
    fontFamily: Fonts.sansBold, fontSize: 14.5, color: Palette.cream,
  },

  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.four,
    paddingTop: 12, paddingBottom: Spacing.four,
  },
  cardBackBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 12, paddingHorizontal: 18,
    borderRadius: 999,
  },
  cardBackBtnText: {
    fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.inkSoft,
    letterSpacing: 0.2,
  },
  skipBtn: {
    paddingVertical: 12, paddingHorizontal: 22,
    borderRadius: 999,
  },
  skipBtnText: {
    fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.inkSoft,
    letterSpacing: 0.2,
  },

  emptyWrap: {
    flex: 1, paddingHorizontal: 32, paddingTop: 60,
    alignItems: 'center', gap: 14,
  },
  emptyIcon: {
    width: 84, height: 84, borderRadius: 42,
    backgroundColor: 'rgba(225,163,61,0.18)',
    alignItems: 'center', justifyContent: 'center',
  },
  emptyTitle: {
    fontFamily: Fonts.serifBold, fontSize: 20, color: Palette.ink,
    letterSpacing: -0.3, textAlign: 'center',
  },
  emptyBody: {
    fontFamily: Fonts.sansMedium, fontSize: 13.5, color: Palette.inkSoft,
    textAlign: 'center', lineHeight: 20,
  },
  primaryBtn: {
    marginTop: 8,
    paddingVertical: 13, paddingHorizontal: 22,
    borderRadius: 14, backgroundColor: Palette.forest,
  },
  primaryBtnText: {
    fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.cream,
  },
});
